<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :open-dialog="openDialog"
    :open-filter="openFilter"
    @reload="getData"
  >
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="modal-style">
          <h3>{{ this.$t("components.OperatorsLogs.question.title") }}</h3>
          <p>{{ this.$t("components.OperatorsLogs.question.description") }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="filter"
      width="35vw"
      height="40vh"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="filter = false"
          />
        </v-card-actions>
        <div class="internal-style">
          <MultiSelectFilter
            v-model="selectedPages"
            class="mr-3"
            :select-items="getOptions"
            :label="pages"
            :searchable="true"
          />
          <div class="buttons">
            <DsButton
              v-tooltip="filterLabel"
              class="clean-sla-buttons"
              style="margin: 0.5rem; border: none"
              color="secondary"
              name="filter"
              :icon="true"
              @click="applyFilter"
            />
            <DsButton
              v-tooltip="trashLabel"
              class="clean-sla-buttons"
              style="margin: 0.5rem"
              color="secondary"
              name="trash"
              :icon="true"
              @click="resetFilter"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>

    <template #top-right-slot>
      <Badge
        v-if="hasFilter"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
      <DsButton
        v-tooltip="filterButton"
        color="tertiary"
        name="filter"
        :icon="true"
        @click="openFilter"
      />
      <DsButton
        v-tooltip="infoLabel"
        color="tertiary"
        name="questions"
        :icon="true"
        @click="openDialog"
      />
    </template>

    <v-row
      no-gutters
      class="gauge-style"
      justify="center"
    >
      <PercentGauge
        v-if="!noScore"
        :score="score"
        :item-id="itemId"
        :title="title"
        :request-status="requestStatus"
        @reload="$emit('reload', $event)"
      />

      <h2
        v-else
        class="no-post-title pt-16"
      >
        {{ noScoreLabel }}
      </h2>
    </v-row>
  </CardWithTitle>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';
import sentimentModel from '@/mixins/sentiment-model';
import EventBus from '@/utils/event-bus';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import cardsModel from '@/mixins/cards-model';
import { DsButton } from '@ds/components';
import PercentGauge from '@/components/cards/models/PercentGauge';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import Badge from '@/components/Badge';

export default {
  name: 'SLAGaugeChart',
  components: {
    CardWithTitle,
    PercentGauge,
    DsButton,
    MultiSelectFilter,
    Badge,
  },
  mixins: [sentimentModel, cardsModel],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      filter: false,
      hasFilter: false,
      selectedPages: null,
      pages: this.$t('components.OperatorsLogs.pagesName'),
      infoLabel: this.$t('components.OperatorsLogs.infoLabel'),
      closeLabel: this.$t('components.OperatorsLogs.closeLabel'),
      filterLabel: this.$t('components.OperatorsLogs.filterLabel'),
      trashLabel: this.$t('components.OperatorsLogs.trashLabel'),
      filterButton: this.$t('components.OperatorsLogs.filterLabel'),
      noScoreLabel: this.$t('components.OperatorsLogs.noSLAScoreLabel'),
      score: { value: 0 },
      requestStatus: 'loading',
      noScore: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    getOptions() {
      return this.activeDashboard?.data?.pages.map((res) => res.username) || [];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', ['getOperatorsLogsService']),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.requestStatus = 'loading';
      this.filter = false;
      const pages = this.selectedPages?.value
        ? this.selectedPages.value.map((res) => {
          const selectPage = this.activeDashboard.data.pages.find(
            (element) => element.username === res
          );
          if (selectPage) {
            return selectPage.page_id;
          }
          return null;
        })
        : null;
      this.getOperatorsLogsService({
        itemId: this.itemId,
        servicePath: 'getSLA',
        serviceParams: {
          page_ids: this.selectedPages ? pages.join(',') : null,
        },
      }).then((res) => {
        this.noScore = Boolean(!res?.slascore);
        this.score = { value: res.slascore * 100 };
        this.requestStatus = 'success';
      }).catch(error => {
        console.error(error);
        this.requestStatus = 'error';
      });

      if (options?.silentRefresh) {
        EventBus.$emit('updated', { itemId: this.itemId });
      }
    },
    applyFilter(options) {
      this.hasFilter = true;
      this.getData(options);
    },
    openDialog() {
      this.dialog = true;
    },
    openFilter() {
      this.filter = true;
    },
    resetFilter() {
      this.selectedPages = null;
      this.requestStatus = 'loading';
      this.hasFilter = false;
      this.getData();
    },
  },
};
</script>

<style lang="scss" scoped>
.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.internal-style {
  width: 100%;
  padding: 1rem;
  display: flex;
}

.gauge-style {
  position: relative;
  width: 100%;
  height: 100%;
}

.buttons {
  display: flex;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
  justify-content: flex-end;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }

  .close-icon {
    height: var(--ds-spacing-stack-lg);
    padding-top: var(--ds-spacing-stack-lg);
  }

  .v-dialog {
    overflow-x: hidden;
  }
}
</style>

